import React, { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { theme } from "../../assets/theme/theme";

const TripStepperTimeline = ({ steps = [], isTripComplete = false }) => {
  const stepItemRefs = useRef([]);

  /* ---------------- GENERATE LABELS (P1, P2, D1...) ---------------- */
  let pickupCount = 0;
  let dropoffCount = 0;
  const labeledSteps = steps.map((s) => {
    if (s.type === "PICKUP") {
      pickupCount += 1;
      return { ...s, label: `P${pickupCount}`, subLabel: `Pickup ${pickupCount}` };
    }
    dropoffCount += 1;
    return { ...s, label: `D${dropoffCount}`, subLabel: `Drop ${dropoffCount}` };
  });

  const activeStepIndex = isTripComplete
    ? -1
    : labeledSteps.findIndex((s) => s.status !== "COMPLETED");

  /* ---------------- AUTO-SCROLL ACTIVE NODE ---------------- */
  useEffect(() => {
    if (activeStepIndex >= 0 && stepItemRefs.current[activeStepIndex]) {
      stepItemRefs.current[activeStepIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeStepIndex, isTripComplete]);

  if (!labeledSteps.length) return null;

  return (
    <div className="px-2 w-full pt-2 pb-1.5">
      {/* 3-Point Horizontal Viewport Container */}
      <div
        className="overflow-x-auto no-scrollbar scroll-smooth px-2 py-1"
        style={{ scrollSnapType: "x mandatory" }}
      >
        <div
          className={`flex items-start ${
            labeledSteps.length <= 3 ? "w-full justify-between" : "w-max min-w-full"
          }`}
        >
          {labeledSteps.map((s, idx) => {
            const isCompleted = isTripComplete || s.status === "COMPLETED";
            const isActive = !isTripComplete && idx === activeStepIndex;

            return (
              <React.Fragment key={s.step || idx}>
                {/* 1. Milestone Column Node */}
                <div
                  ref={(el) => (stepItemRefs.current[idx] = el)}
                  className="flex flex-col items-center shrink-0"
                  style={{
                    width: labeledSteps.length > 3 ? "80px" : "auto",
                    scrollSnapAlign: "center",
                  }}
                >
                  {/* Step Circle (Height = 32px) */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-transform duration-200 z-10"
                    style={{
                      fontFamily: "DMMono-Medium",
                      backgroundColor: isCompleted
                        ? theme.colors.success
                        : isActive
                        ? theme.colors.brand
                        : theme.colors.pureWhite,
                      color:
                        isCompleted || isActive
                          ? theme.colors.pureWhite
                          : theme.colors.concrete,
                      border: `2px solid ${
                        isCompleted
                          ? theme.colors.success
                          : isActive
                          ? theme.colors.brand
                          : theme.colors.grey002
                      }`,
                      boxShadow: isActive
                        ? `0 2px 10px ${theme.colors.brand}40`
                        : "0 1px 3px rgba(0,0,0,0.04)",
                      transform: isActive ? "scale(1.08)" : "scale(1)",
                    }}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4 stroke-[3]" />
                    ) : (
                      s.label
                    )}
                  </div>

                  {/* Sub-label */}
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider mt-1.5 leading-tight select-none"
                    style={{
                      fontFamily: "DMSans-Medium",
                      color: isCompleted
                        ? theme.colors.success
                        : isActive
                        ? theme.colors.bitumen
                        : theme.colors.concrete,
                    }}
                  >
                    {s.subLabel}
                  </span>
                </div>

                {/* 2. Pixel-Perfect Centered Connecting Rail */}
                {idx < labeledSteps.length - 1 && (
                  <div
                    className="flex-1 self-start min-w-[32px] mx-1"
                    style={{
                      marginTop: "15px", // Dead-center: (32px circle height / 2) - 1px border
                      borderTop: `2px dashed ${
                        isCompleted ? theme.colors.success : theme.colors.grey002
                      }`,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TripStepperTimeline;